import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Mail, Phone, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { db } from './firebase-config';
import { collection, getDocs } from 'firebase/firestore';
import EventCard from './EventCard';
import { SkeletonEventGrid } from './components/Spinner';

const CommunityDetailPage = ({ communities }) => {
  const { id } = useParams();
  const community = communities.find((c) => c.id === id);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 9;

  useEffect(() => {
    const fetchCommunityEvents = async () => {
      setLoading(true);
      try {
        const eventsRef = collection(db, 'events');
        const eventsSnapshot = await getDocs(eventsRef);
        const eventsList = eventsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((event) => event.organizer?.name === community?.name);
        setEvents(eventsList);
      } catch (error) {
        console.error('Failed to fetch events:', error);
      } finally {
        setLoading(false);
      }
    };

    if (community) {
      fetchCommunityEvents();
    }
  }, [community]);

  if (!community) {
    return (
      <div className="flex flex-col justify-center items-center text-center px-4 mt-0">
        <h1 className="text-4xl md:text-6xl font-extrabold animate-gradient-text leading-tight mb-6">
          Community Not Found
        </h1>
        <img src="/404-desktop.png" alt="404 Not Found" className="hidden md:!block w-full max-w-lg mb-6" />
        <img src="/404-mobile.png" alt="404 Not Found" className="block md:!hidden w-full max-w-sm mb-6" />
        <p className="text-lg text-gray-200 mb-6">
          The Community you're looking for doesn't exist or has been moved.
        </p>
        <Link
          to="/"
          className="px-6 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors font-medium"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  // Pagination logic
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = events.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(events.length / eventsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Link to="/communities" className="text-blue-400 hover:text-blue-300 mb-6 inline-block">
        <ChevronLeft className="inline-block mr-2" size={18} />
        Back to Communities
      </Link>

      <div className="bg-[#14181D] rounded-2xl shadow-xl p-8">
        <div className="flex flex-col items-center mb-8">
          <img
            src={community.avatar || '/placeholder-avatar.svg'}
            alt={community.name}
            className="w-28 h-28 rounded-full object-cover mb-4"
          />
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-white">{community.name}</h1>
            {[4, 5, 6].includes(community.verified) && (
              <span
                className={`ml-1 ${
                  community.verified === 4
                    ? 'verified-badge-user'
                    : community.verified === 5
                    ? 'verified-badge-admin'
                    : 'verified-badge-community'
                }`}
              >
                <CheckCircle className="text-blue-500" size={18} />
              </span>
            )}
          </div>
        </div>

        {/* Centered bio, posts, and contact details */}
        <div className="flex flex-col items-center text-center gap-6">
          <p className="text-gray-300 max-w-2xl">{community.description}</p>
          {community.posts !== undefined && (
            <div className="text-gray-300">
              <span className="font-semibold text-white">Posts:</span> {community.posts}
            </div>
          )}
          {community.last && (
            <div className="text-gray-300">
              <span className="font-semibold text-white">Last Active:</span> {community.last}
            </div>
          )}
          {community.site && (
            <div className="flex items-center justify-center text-gray-300">
              <ExternalLink size={18} className="mr-2 text-blue-400" />
              <span className="font-semibold text-white mr-1">Website:</span>
              <a
                href={community.site}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline truncate max-w-xs"
              >
                {community.site}
              </a>
            </div>
          )}
          {community.email && (
            <div className="flex items-center justify-center text-gray-300">
              <Mail size={18} className="mr-2 text-blue-400" />
              <span className="font-semibold text-white mr-1">Email:</span>
              {community.email}
            </div>
          )}
          {community.phone && (
            <div className="flex items-center justify-center text-gray-300">
              <Phone size={18} className="mr-2 text-blue-400" />
              <span className="font-semibold text-white mr-1">Phone:</span>
              {community.phone}
            </div>
          )}
          {!community.site && !community.email && !community.phone && (
            <div className="text-gray-400 italic">No contact information provided.</div>
          )}
        </div>
      </div>

      {/* Events Section */}
      <section className="mt-12">
        <h2 className="text-3xl font-bold mb-6 text-white text-center">Community Events</h2>
        {loading ? (
          <SkeletonEventGrid />
        ) : (
          <>
            {currentEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6">
                <img src="/Notfound.svg" alt="No events" className="w-48 h-48 object-cover mb-4" />
                <p className="text-gray-200">No events available for this community.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center lg:justify-items-start">
                  {currentEvents.map((event) => {
                    const eventDate = new Date(event.date);
                    const isPastEvent = eventDate < today;
                    return (
                      <div
                        key={`event-${event.id}`}
                        className={isPastEvent ? 'opacity-50 grayscale' : ''}
                      >
                        <EventCard event={event} />
                      </div>
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-6">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full disabled:opacity-40 hover:bg-blue-600 transition"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="text-sm text-gray-300 font-medium px-3 py-1 rounded-full bg-[#1F2937]">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center bg-gray-800 text-white rounded-full disabled:opacity-40 hover:bg-blue-600 transition"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default CommunityDetailPage;